import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Badge } from "../Badge";
import { CalendarClock, ArrowRight, HeartHandshake, Phone } from "lucide-react";
import { formatDate, formatTime } from "../caregiver/CaregiverDashboard";
import { CONTACT } from "../../constants/data";

export default function PatientDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/patient/overview").then((r) => setData(r.data)).catch(() => setData(false));
  }, []);

  if (data === null) return <p className="text-slate-500 font-semibold">Loading your details…</p>;
  if (data === false) return <p className="text-red-700 font-semibold">Unable to load your dashboard. Please contact the office.</p>;

  const next = data.next_visit;

  return (
    <div data-testid="patient-dashboard">
      <h1 className="font-display text-3xl md:text-4xl text-brand-navy">Hello, {data.patient?.full_name?.split(" ")[0]}</h1>
      <p className="mt-2 text-[15px] text-slate-600">Here is your care schedule and support information.</p>

      <section className="mt-8">
        <h2 className="font-display text-2xl text-brand-navy">Your Next Visit</h2>
        {next ? (
          <div data-testid="patient-next-visit" className="mt-4 rounded-2xl bg-brand-navy text-white p-6 md:p-8 shadow-[0_16px_40px_rgb(11,27,61,0.25)]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-cyan">Next Scheduled Visit</p>
                <p className="mt-2 font-display text-2xl md:text-3xl">{formatDate(next.date)}</p>
              </div>
              <Badge kind="visit" value={next.status} />
            </div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-[15px] text-slate-200">
              <p><strong className="text-white">Time:</strong> {formatTime(next.start_time)} – {formatTime(next.end_time)}</p>
              <p><strong className="text-white">Type of Care:</strong> {next.care_type}</p>
              <p className="sm:col-span-2"><strong className="text-white">Your Caregiver:</strong> {next.caregiver_name}</p>
            </div>
            <Link
              to="/patient/visits"
              data-testid="patient-all-visits-link"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-teal px-6 py-3 text-[15px] font-bold text-white hover:bg-brand-cyan hover:text-brand-navy transition-colors duration-300 active:scale-95"
            >
              View All My Visits <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
            No visits are scheduled right now. Our office team will be in touch to arrange your care.
          </div>
        )}
      </section>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-xl text-brand-navy flex items-center gap-2">
            <HeartHandshake className="h-5 w-5 text-brand-teal" strokeWidth={1.6} /> Your Care Team
          </h2>
          {data.caregivers.length ? (
            <ul className="mt-4 space-y-2">
              {data.caregivers.map((c) => (
                <li key={c} className="text-[15px] font-semibold text-brand-navy">{c}</li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-slate-500">Your caregiver will be assigned once visits are scheduled.</p>
          )}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6">
          <h2 className="font-display text-xl text-brand-navy flex items-center gap-2">
            <Phone className="h-5 w-5 text-brand-teal" strokeWidth={1.6} /> Need Help?
          </h2>
          <p className="mt-3 text-[15px] text-slate-600">Questions about your care or schedule? Our office team is happy to help.</p>
          <a href={CONTACT.phoneHref} data-testid="patient-call-office" className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-teal px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-navy transition-colors duration-300 active:scale-95">
            <Phone className="h-4 w-4" /> Call {CONTACT.phone}
          </a>
          <p className="mt-3 text-sm text-slate-500">{CONTACT.email}</p>
        </section>
      </div>

      <section className="mt-8">
        <h2 className="font-display text-2xl text-brand-navy">Upcoming Visits</h2>
        <ul className="mt-4 space-y-3">
          {data.upcoming.length === 0 && <li className="rounded-2xl border border-slate-200 bg-white p-6 text-slate-500">No upcoming visits.</li>}
          {data.upcoming.map((v) => (
            <li key={v.id} data-testid={`patient-visit-${v.id}`} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4">
              <div className="min-w-0">
                <p className="font-bold text-brand-navy">{formatDate(v.date)}</p>
                <p className="text-sm text-slate-500">{formatTime(v.start_time)} – {formatTime(v.end_time)} · {v.care_type} · {v.caregiver_name}</p>
              </div>
              <Badge kind="visit" value={v.status} />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
